/**
 * @fileoverview Password-reset request mutation hook.
 * @description Wraps the forgot-password API call in a TanStack mutation for loading/error/
 * success state, so the modal can render the right view without owning the request itself.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation } from '@tanstack/react-query'

import {
  requestPasswordReset,
  type PasswordResetRequest,
  type PasswordResetResponse,
} from '@/features/auth/services/password-reset.service'

import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Submits a password-reset request for the given email address.
 * @returns {ReturnType<typeof useMutation<PasswordResetResponse, ApiError, PasswordResetRequest>>}
 * The mutation object (mutate, isPending, isSuccess, error, ...).
 */
export function useRequestPasswordReset() {
  return useMutation<PasswordResetResponse, ApiError, PasswordResetRequest>({
    mutationFn: requestPasswordReset,
  })
}
